import type { TeamMember } from '../../store/api/apiSlice';
import { getMemberDisplayName } from './types';

export type MemberListFilter = 'active-and-chem' | 'past-members' | 'past-chem-calls';

export const MEMBER_LIST_FILTERS: MemberListFilter[] = [
  'active-and-chem',
  'past-members',
  'past-chem-calls',
];

function matchesFilter(member: TeamMember, filter: MemberListFilter): boolean {
  const relationshipType = member.relationship_type;

  switch (filter) {
    case 'active-and-chem':
      return (
        (relationshipType === undefined && member.active) ||
        relationshipType === 'active' ||
        relationshipType === 'active_chemistry_only'
      );
    case 'past-members':
      return relationshipType === 'past_member';
    case 'past-chem-calls':
      return relationshipType === 'past_chemistry_call';
  }
}

function compareByFirstNameThenDisplayName(a: TeamMember, b: TeamMember): number {
  const aFirst = a.person.given_name?.trim().toLowerCase();
  const bFirst = b.person.given_name?.trim().toLowerCase();

  if (aFirst && bFirst && aFirst !== bFirst) {
    return aFirst.localeCompare(bFirst);
  }

  return getMemberDisplayName(a).toLowerCase().localeCompare(getMemberDisplayName(b).toLowerCase());
}

function chemistryCallTime(member: TeamMember): number | null {
  const value = member.chemistry_call_at;
  if (!value) return null;

  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? null : timestamp;
}

function compareByChemistryCallNewestThenName(a: TeamMember, b: TeamMember): number {
  const aTimestamp = chemistryCallTime(a);
  const bTimestamp = chemistryCallTime(b);

  if (aTimestamp !== null && bTimestamp !== null && aTimestamp !== bTimestamp) {
    return bTimestamp - aTimestamp;
  }
  if (aTimestamp !== null && bTimestamp === null) {
    return -1;
  }
  if (aTimestamp === null && bTimestamp !== null) {
    return 1;
  }

  return compareByFirstNameThenDisplayName(a, b);
}

function compareMembersForFilter(
  filter: MemberListFilter
): (a: TeamMember, b: TeamMember) => number {
  return filter === 'past-chem-calls'
    ? compareByChemistryCallNewestThenName
    : compareByFirstNameThenDisplayName;
}

export function buildMemberListView(members: TeamMember[], filter: MemberListFilter) {
  const filtered = [...members]
    .filter((member) => matchesFilter(member, filter))
    .sort(compareMembersForFilter(filter));

  return { members: filtered };
}

export function getPreferredMemberListFilter(
  members: TeamMember[],
  currentFilter: MemberListFilter
): MemberListFilter {
  const filterCounts = new Map(
    MEMBER_LIST_FILTERS.map((filter) => {
      const view = buildMemberListView(members, filter);
      return [filter, view.members.length] as const;
    })
  );

  if ((filterCounts.get(currentFilter) ?? 0) > 0) {
    return currentFilter;
  }

  return MEMBER_LIST_FILTERS.find((filter) => (filterCounts.get(filter) ?? 0) > 0) ?? currentFilter;
}
